import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import type * as echarts from 'echarts';

import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { BridgeSign, BridgeStep, BridgeStepType, DashboardTile, WaterfallBar } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import {
  applyStepSignsToWaterfallBars, buildWaterfallPreviewOption, buildWaterfallTileData, sanitizeStepForPayload, waterfallTileFromResponse,
  withWaterfallXAxisLabelRotation,
} from './waterfall-tile.utils';

/**
 * Waterfall (bridge) build form (WATERFALL): an ordered list of START /
 * CHANGE / TOTAL steps, each a dataset column + aggregation or a formula,
 * plus the shared Format accordion. Computed by the backend; the shell runs
 * the default Generate flow (POST → tile from response).
 */
@Component({
  selector: 'app-waterfall-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, NgSelectModule],
  templateUrl: './waterfall-step.component.html',
  styles: [':host { display: contents; }'],
})
export class WaterfallStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['WATERFALL'] as const;

  steps: BridgeStep[] = [];
  private stepIdCounter = 0;

  get stepTypes(): LabelValueOption[] {
    return this.host.layout.options?.stepTypes?.length
      ? this.host.layout.options.stepTypes
      : [
          { label: 'Start (anchor pillar)', value: 'START' },
          { label: 'Change (floating segment)', value: 'CHANGE' },
          { label: 'Total (subtotal / milestone)', value: 'TOTAL' },
        ];
  }
  get layoutAggregations(): LabelValueOption[] {
    return this.host.layout.options?.layoutAggregations?.length
      ? this.host.layout.options.layoutAggregations
      : [{ label: 'Sum', value: 'SUM' }, { label: 'Average', value: 'AVG' }];
  }
  get signOptions(): LabelValueOption[] {
    return this.host.layout.options?.signOptions?.length
      ? this.host.layout.options.signOptions
      : [
          { label: 'Auto (let the query decide)', value: 'AUTO' },
          { label: 'Positive (adds)', value: 'POSITIVE' },
          { label: 'Negative (subtracts)', value: 'NEGATIVE' },
        ];
  }

  addStep(): void {
    const step: BridgeStep = {
      id: this.nextStepId(),
      stepType: this.steps.length === 0 ? 'START' : 'CHANGE',
      label: this.steps.length === 0 ? 'Starting value' : `Step ${this.steps.length + 1}`,
      dataset: this.host.selectedDatasets[0] || '',
      field: '',
      aggregation: 'SUM',
      sign: 'AUTO',
      formula: '',
      formulaMode: false,
    };
    this.steps = [...this.steps, step];
    if (step.dataset) this.host.loadColumnsForDataset(step.dataset);
  }

  removeStep(step: BridgeStep): void {
    this.steps = this.steps.filter((item) => item.id !== step.id);
    this.host.forgetRowColumns(step.id);
    this.host.invalidatePreview();
  }

  moveStepUp(index: number): void {
    if (index <= 0) return;
    this.reorderStep(index, index - 1);
  }

  moveStepDown(index: number): void {
    if (index >= this.steps.length - 1) return;
    this.reorderStep(index, index + 1);
  }

  private reorderStep(fromIndex: number, toIndex: number): void {
    const next = [...this.steps];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.steps = next;
    this.host.invalidatePreview();
  }


  onStepTypeChange(step: BridgeStep): void {
    if (step.stepType === 'TOTAL') {
      step.formulaMode = true;
      step.dataset = '';
      step.field = '';
      step.aggregation = '';
      step.sign = 'AUTO';
      step.formula = step.formula || '';
    } else {
      step.formulaMode = (step.stepType === 'CHANGE' || step.stepType === 'START') ? !!step.formulaMode : false;
      if (!step.formulaMode) step.formula = '';
      if (!step.dataset) {
        step.dataset = this.host.selectedDatasets[0] || '';
      }
      if (!step.aggregation) {
        step.aggregation = 'SUM';
      }
      step.sign = step.stepType === 'START' ? 'AUTO' : (step.sign || 'AUTO');
      if (step.dataset) this.host.loadColumnsForDataset(step.dataset);
    }
    this.steps = [...this.steps];
    this.host.invalidatePreview();
  }

  toggleStepFormulaMode(step: BridgeStep): void {
    if (step.stepType !== 'CHANGE' && step.stepType !== 'START') return;
    step.formulaMode = !step.formulaMode;
    if (step.formulaMode) {
      step.dataset = '';
      step.field = '';
      step.aggregation = '';
    } else {
      step.formula = '';
      step.dataset = this.host.selectedDatasets[0] || '';
      step.aggregation = 'SUM';
      if (step.dataset) this.host.loadColumnsForDataset(step.dataset);
    }
    this.steps = [...this.steps];
    this.host.invalidatePreview();
  }

  onStepDatasetChange(step: BridgeStep, dataset: string | null): void {
    step.dataset = dataset || '';
    step.field = '';
    this.steps = [...this.steps];
    if (step.dataset) this.host.loadColumnsForDataset(step.dataset);
    this.host.invalidatePreview();
  }

  onStepFieldChange(step: BridgeStep, field: string | null): void {
    step.field = field || '';
    this.steps = [...this.steps];
    this.host.invalidatePreview();
  }

  onStepFormulaOrLabelChange(): void {
    this.steps = [...this.steps];
    this.host.invalidatePreview();
  }

  isStepColumnsLoading(step: BridgeStep): boolean {
    return this.host.isDatasetColumnsLoading(step.dataset);
  }

  stepFieldPlaceholder(step: BridgeStep): string {
    if (!step.dataset) return 'Select a dataset first';
    return this.isStepColumnsLoading(step) ? 'Loading columns...' : 'Select field';
  }

  columnsForStep(step: BridgeStep): string[] {
    return this.host.columnsForRow(step.id, step.dataset);
  }

  onStepFieldDrop(event: DragEvent, step: BridgeStep): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping || step.stepType === 'TOTAL') return;
    if (step.stepType === 'CHANGE' && step.formulaMode) {
      step.formula = this.host.appendFormulaToken(step.formula, mapping.field);
      this.onStepFormulaOrLabelChange();
      return;
    }
    if (!this.host.selectedDatasets.includes(mapping.dataset)) this.host.selectedDatasets = [...this.host.selectedDatasets, mapping.dataset];
    step.dataset = mapping.dataset;
    step.field = mapping.field;
    this.host.loadColumnsForDataset(mapping.dataset);
    this.steps = [...this.steps];
    this.host.invalidatePreview();
  }

  onWaterfallFormulaDrop(step: BridgeStep, event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    step.formula = this.host.appendFormulaToken(step.formula, mapping.field);
    this.onStepFormulaOrLabelChange();
  }

  private nextStepId(): string {
    this.stepIdCounter += 1;
    return `bridge-step-${this.stepIdCounter}`;
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    return this.steps.length ? [] : ['At least one step'];
  }

  buildPayload(): Record<string, unknown> {
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: this.host.componentType,
      chartType: this.host.selectedLayoutType,
      title: this.host.title.trim(),
      datasets: [...this.host.selectedDatasets],
      steps: this.steps.map((step) => sanitizeStepForPayload(step)),
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const savedSteps = Array.isArray(payload['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
    this.steps = savedSteps.map((step) => ({
      id: this.nextStepId(),
      stepType: String(step['stepType'] ?? 'CHANGE') as BridgeStepType,
      label: String(step['label'] ?? ''),
      dataset: String(step['dataset'] ?? ''),
      field: String(step['field'] ?? ''),
      aggregation: String(step['aggregation'] ?? ''),
      sign: String(step['sign'] ?? 'AUTO') as BridgeSign,
      formula: String(step['formula'] ?? ''),
      formulaMode: !!step['formula'] || String(step['stepType']) === 'TOTAL',
    }));
    this.steps.forEach((step) => { if (step.dataset) this.host.loadColumnsForDataset(step.dataset); });
    this.host.barFormat = { ...(tile.format || {}) };
  }

  reset(): void {
    this.steps = [];
  }

  /** Steps whose dataset was dropped from the selection fall back to the primary dataset. */
  onDatasetsChanged(): void {
    const valid = new Set(this.host.selectedDatasets);
    const primary = this.host.drillDataset;
    let changed = false;
    this.steps.forEach((step) => {
      if (step.dataset && !valid.has(step.dataset)) {
        step.dataset = primary;
        step.field = '';
        changed = true;
      }
    });
    if (changed) {
      if (primary) this.host.loadColumnsForDataset(primary);
      this.steps = [...this.steps];
      this.host.invalidatePreview();
    }
  }

  /** The Format accordion changed: re-render the waterfall tile being edited with the new format. */
  onFormatChanged(): void {
    if (!this.host.editingTileId) return;
    const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
    const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'WATERFALL') return;
    const updated: DashboardTile = {
      ...tile,
      format: { ...(tile.format || {}), ...this.host.barFormat, title: this.host.title.trim() || undefined },
    };
    this.host.onTileFormatChange(updated);
    this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? updated : t));
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null, payload: Record<string, unknown>): DashboardTile {
    return waterfallTileFromResponse(base, response, payload, this.host.title.trim() || undefined);
  }

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    tile.previewOption = tile.waterfallBars
      ? buildWaterfallPreviewOption(tile.waterfallBars, format)
      : withWaterfallXAxisLabelRotation(tile.previewOption, format) as echarts.EChartsOption | undefined;
    tile.waterfallData = buildWaterfallTileData(tile);
  }

  applyRecomputeResult(tile: DashboardTile, { result, format }: TileRecomputeResult): boolean {
    const rawBars = (Array.isArray(result['data'])
      ? result['data']
      : ((result['data'] as { data?: WaterfallBar[] })?.data || [])) as WaterfallBar[];
    const bars = applyStepSignsToWaterfallBars(rawBars, tile.payload);
    tile.waterfallBars = bars;
    tile.previewOption = buildWaterfallPreviewOption(bars, format);
    tile.waterfallData = buildWaterfallTileData(tile);
    return true;
  }
}
